import { Injectable, inject } from '@angular/core';
import { LibraryService, IBizEntityWithAccessUrl } from '../pages/libraries/library.service';
import { Html2CanvasThumbnailHelper } from '../shared/utils/html2canvas-thumbnail.helper';
import { BizEntityFunctionTypes, BizEntityTypeCategory, IBizEntity, LoggerService } from '@optima-stride-ai/core';
import { DefaultContent, LogMessages, FileExtensions, FileTypeCategories, UIMessages } from '../shared/enums/app-constants';

@Injectable({
  providedIn: 'root'
})
export class FileCreationService {
  private readonly libraryService = inject(LibraryService);
  private readonly logger = inject(LoggerService);

  /**
   * Create a new document file
   */
  async createDocument(item: IBizEntity | any, fileName: string, selectedParentId: string, bizEntity?: IBizEntity): Promise<IBizEntityWithAccessUrl> {
    try {
      await this.validateStructuralParentEntity(selectedParentId);
      this.logger.debug(`${LogMessages.PROCESS_CREATING_DOCUMENT}: ${fileName}`);
      
      // Create blank document using the library service
      const newFile = await this.libraryService.createBlankFile(FileTypeCategories.DOCUMENT, item, fileName, selectedParentId, bizEntity);
      
      this.logger.info(`${LogMessages.SUCCESS_FILE_CREATED}: ${newFile.name}`);
      return newFile;
    } catch (error) {
      this.logger.error(`${LogMessages.ERROR_FILE_CREATION}: ${error}`);
      throw error;
    }
  }

  /**
   * Create a new spreadsheet file
   */
  async createSpreadsheet(item: IBizEntity | any, fileName: string, selectedParentId: string, params?: any): Promise<IBizEntityWithAccessUrl> {
    try {
      await this.validateStructuralParentEntity(selectedParentId);
      this.logger.debug(`${LogMessages.PROCESS_CREATING_SPREADSHEET}: ${fileName}`);
      
      const newFile = await this.libraryService.createBlankFile(FileTypeCategories.SPREADSHEET, item, fileName, selectedParentId, params);
      
      this.logger.info(`${LogMessages.SUCCESS_FILE_CREATED}: ${newFile.name}`);
      return newFile;
    } catch (error) {
      this.logger.error(`${LogMessages.ERROR_FILE_CREATION}: ${error}`);
      throw error;
    }
  }

    /**
   * Create a new CSV file
   */
  async createCsv(item: IBizEntity | any, fileName: string, selectedParentId: string, params?: any): Promise<IBizEntityWithAccessUrl> {
    try {
      await this.validateStructuralParentEntity(selectedParentId);
      this.logger.debug(`${LogMessages.PROCESS_CREATING_CSV}: ${fileName}`);
      
      const newFile = await this.libraryService.createBlankFile(FileTypeCategories.CSV, item, fileName, selectedParentId, params);
      
      this.logger.info(`${LogMessages.SUCCESS_FILE_CREATED}: ${newFile.name}`);
      return newFile;
    } catch (error) {
      this.logger.error(`${LogMessages.ERROR_FILE_CREATION}: ${error}`);
      throw error;
    }
  }

  /**
   * Create a new markdown file with thumbnail
   */
  async createMarkdown(item: IBizEntity | any, fileName: string, selectedParentId: string, params?: any): Promise<IBizEntityWithAccessUrl> {
    try {
      await this.validateStructuralParentEntity(selectedParentId);
      this.logger.debug(`${LogMessages.PROCESS_CREATING_MARKDOWN}: ${fileName}`);
      
      // Create the blank file first
      const newFile = await this.libraryService.createBlankFile(FileTypeCategories.MARKDOWN, item, fileName, selectedParentId, params);
      
      // Generate thumbnail using the same method used for updates
      const defaultContent = `# ${fileName.replace(FileExtensions.MD, '')}\n\n${DefaultContent.MARKDOWN_TEMPLATE}`;
      
      try {
        this.logger.debug(LogMessages.PROCESS_GENERATING_MARKDOWN);
        // Generate thumbnail without caching
        const thumbnail = Html2CanvasThumbnailHelper.createMarkdownThumbnailFromContent(defaultContent, fileName);
        
        if (thumbnail) {
          this.logger.debug(LogMessages.SUCCESS_THUMBNAIL_GENERATED);
        } else {
          this.logger.warn(LogMessages.WARNING_THUMBNAIL_FAILED);
        }
      } catch (error) {
        this.logger.error(`${LogMessages.ERROR_MARKDOWN_THUMBNAIL}: ${error}`);
        // Don't fail file creation if thumbnail generation fails
      }
      
      this.logger.info(`${LogMessages.SUCCESS_FILE_CREATED}: ${newFile.name}`);
      return newFile;
    } catch (error) {
      this.logger.error(`❌ Failed to create markdown: ${error}`);
      throw error;
    }
  }

  /**
   * Create a new presentation file
   */
  async createPresentation(item: IBizEntity | any, fileName: string, selectedParentId: string, params?: any): Promise<IBizEntityWithAccessUrl> {
    try {
      await this.validateStructuralParentEntity(selectedParentId);
      this.logger.debug(`${LogMessages.PROCESS_CREATING_PRESENTATION}: ${fileName}`);
      
      const newFile = await this.libraryService.createBlankFile(FileTypeCategories.PRESENTATION, item, fileName, selectedParentId, params);
      
      this.logger.info(`${LogMessages.SUCCESS_FILE_CREATED}: ${newFile.name}`);
      return newFile;
    } catch (error) {
      this.logger.error(`${LogMessages.ERROR_FILE_CREATION}: ${error}`);
      throw error;
    }
  }

  /**
   * Create a new diagram/drawing file
   */
  async createDiagram(item: IBizEntity | any, fileName: string, selectedParentId: string, params?: any): Promise<IBizEntityWithAccessUrl> {
    try {
      await this.validateStructuralParentEntity(selectedParentId);
      this.logger.debug(`🎨 Creating new diagram: ${fileName}`);
      
      const newFile = await this.libraryService.createBlankFile(FileTypeCategories.DOCUMENT, item, fileName, selectedParentId, params);
      
      this.logger.info(`✅ Diagram created successfully: ${newFile.name}`);
      return newFile;
    } catch (error) {
      this.logger.error(`❌ Failed to create diagram: ${error}`);
      throw error;
    }
  }

    /**
   * Create a new whiteboard file
   */
  async createWhiteboard(item: IBizEntity | any, fileName: string, selectedParentId: string, params?: any): Promise<IBizEntityWithAccessUrl> {
    try {
      await this.validateStructuralParentEntity(selectedParentId);
      this.logger.debug(`🎨 Creating new whiteboard file: ${fileName}`);
      
      const newFile = await this.libraryService.createBlankFile(FileTypeCategories.DOCUMENT, item, fileName, selectedParentId, params);
      
      this.logger.info(`✅ Whiteboard file created successfully: ${newFile.name}`);
      return newFile;
    } catch (error) {
      this.logger.error(`❌ Failed to create whiteboard file: ${error}`);
      throw error;
    }
  }

  /**
   * Create a new PDF document (usually creates as document then exports to PDF)
   */
  async createPDF(item: IBizEntity | any, fileName: string, selectedParentId: string): Promise<IBizEntityWithAccessUrl> {
    try {
      await this.validateStructuralParentEntity(selectedParentId);
      this.logger.debug(`📄 Creating new PDF document: ${fileName}`);
      
      // Create as document first - user can export to PDF later
      const newFile = await this.libraryService.createBlankFile(FileTypeCategories.DOCUMENT, item, fileName, selectedParentId);
      
      this.logger.info(`✅ PDF document created successfully: ${newFile.name}`);
      return newFile;
    } catch (error) {
      this.logger.error(`❌ Failed to create PDF: ${error}`);
      throw error;
    }
  }

  /**
   * Create a markdown file with custom content and generate thumbnail
   */
  async createMarkdownWithContent(
    item: IBizEntity | any, 
    fileName: string, 
    content: string, 
    selectedParentId: string,
    params?: any
  ): Promise<IBizEntityWithAccessUrl> {
    try {
      await this.validateStructuralParentEntity(selectedParentId);
      this.logger.debug(`📝 Creating markdown file with custom content: ${fileName}`);
      
      const newFile = await this.libraryService.createBlankFile(FileTypeCategories.MARKDOWN, item, fileName, selectedParentId, params);
      
      // Note: The thumbnail generation with content upload is handled by the 
      // component's generateMarkdownThumbnailForNewFile method
      
      this.logger.info(`✅ Markdown file with content created successfully: ${newFile.name}`);
      return newFile;
    } catch (error) {
      this.logger.error(`❌ Failed to create markdown with content: ${error}`);
      throw error;
    }
  }

  /**
   * Get the appropriate file extension for a category
   */
  private getFileExtension(category: BizEntityTypeCategory): string {
    switch (category) {
      case BizEntityTypeCategory.HayagrivaFileDoc:
        return FileExtensions.DOCX;
      case BizEntityTypeCategory.HayagrivaFileSpreadsheet:
        return FileExtensions.XLSX;
      case BizEntityTypeCategory.HayagrivaFileMarkdown:
        return FileExtensions.MD;
      case BizEntityTypeCategory.HayagrivaFilePresentation:
        return FileExtensions.PPTX;
      case BizEntityTypeCategory.HayagrivaFileDrawing:
        return FileExtensions.DOCX; // Diagrams are created as documents
      case BizEntityTypeCategory.HayagrivaFilePDF:
        return FileExtensions.DOCX; // PDFs are created as documents first
      case BizEntityTypeCategory.HayagrivaFileCSV:
        return FileExtensions.CSV;
      default:
        return FileExtensions.TXT;
    }
  }

  /**
   * Create file with automatic extension handling
   */
  async createFileWithExtension(
    category: BizEntityTypeCategory, 
    item: IBizEntity | any, 
    baseName: string, 
    selectedParentId: string
  ): Promise<IBizEntityWithAccessUrl> {
    await this.validateStructuralParentEntity(selectedParentId);

    const extension = this.getFileExtension(category);
    const fileName = baseName.endsWith(extension) ? baseName : baseName + extension;

    switch (category) {
      case BizEntityTypeCategory.HayagrivaFileDoc:
        return this.createDocument(item, fileName, selectedParentId);
      case BizEntityTypeCategory.HayagrivaFileSpreadsheet:
        return this.createSpreadsheet(item, fileName, selectedParentId);
      case BizEntityTypeCategory.HayagrivaFileMarkdown:
        return this.createMarkdown(item, fileName, selectedParentId);
      case BizEntityTypeCategory.HayagrivaFilePresentation:
        return this.createPresentation(item, fileName, selectedParentId);
      case BizEntityTypeCategory.HayagrivaFileDrawing:
        return this.createDiagram(item, fileName, selectedParentId);
      case BizEntityTypeCategory.HayagrivaFilePDF:
        return this.createPDF(item, fileName, selectedParentId);
      case BizEntityTypeCategory.HayagrivaFileCSV:
        return this.createCsv(item, fileName, selectedParentId);
      default:
        throw new Error(`Unsupported file category: ${category}`);
    }
  }

  private async validateStructuralParentEntity(parentId: string): Promise<void> {
    if (!parentId) {
      return;
    }

    try {
      const parentEntity = await this.libraryService.getBizEntityById(parentId);
      if (parentEntity?.type?.functionType === BizEntityFunctionTypes.Operational) {
        this.logger.warn(`⚠️ File creation blocked. Parent entity is operational: ${parentId}`);
        throw new Error(UIMessages.STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD);
      }
    } catch (error) {
      if (error instanceof Error && error.message === UIMessages.STRUCTURAL_ENTITY_REQUIRED_FOR_DOCUMENT_UPLOAD) {
        throw error;
      }

      this.logger.warn(`⚠️ Could not validate parent entity type for file creation target ${parentId}: ${error}`);
    }
  }
}
